import { useEffect, useRef, useState } from 'react';

interface AskJeffCommentEditorProps {
  tableResultId: number;
  /** The comment already on record, or null when Jeff has not answered this hand yet. */
  comment: string | null;
  /** The Super Editor's own note about the doubt — read-only context, edited elsewhere. */
  note: string | null;
  sectionLabel: string;
  onSave: (comment: string | null) => Promise<void>;
  onSaveNote: (note: string | null) => Promise<void>;
}

/**
 * The write half of the "Ask Jeff — Review" surface (feature 025): the Super Editor's note about
 * the doubt, and the box where Jeff's answer is recorded.
 *
 * <p>
 * Saving the comment writes to the SHARED cardsgamehands comment store, so it appears as
 * "JEFF SAYS" on every practice surface that serves this hand — that is the whole point of the
 * workflow, and the reason the save confirmation says so out loud.
 * </p>
 */
export function AskJeffCommentEditor({
  tableResultId,
  comment,
  note,
  sectionLabel,
  onSave,
  onSaveNote,
}: AskJeffCommentEditorProps) {
  const [commentDraft, setCommentDraft] = useState(comment ?? '');
  const [noteDraft, setNoteDraft] = useState(note ?? '');
  const [savingComment, setSavingComment] = useState(false);
  const [savingNote, setSavingNote] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Always the freshest props, without making them effect dependencies (see below).
  const latest = useRef({ comment, note });
  latest.current = { comment, note };

  // Re-seed both drafts when the surface moves to ANOTHER HAND — otherwise Previous/Next would
  // carry one hand's text onto the next and risk saving it against the wrong id.
  //
  // Keyed on `tableResultId` alone, deliberately. Including `comment`/`note` also fired this on
  // our OWN save: the page updates the item in place (so the reviewer keeps their position,
  // FR-050), the changed prop re-ran this effect, and `setStatus(null)` wiped the confirmation
  // in the same commit that set it — so "this comment now shows as Jeff says" was never seen.
  // For a given hand the only thing that changes these props is that save, and the draft already
  // holds what was saved, so there is nothing to re-seed from.
  useEffect(() => {
    setCommentDraft(latest.current.comment ?? '');
    setNoteDraft(latest.current.note ?? '');
    setStatus(null);
    setError(null);
  }, [tableResultId]);

  const handleSaveComment = async () => {
    setSavingComment(true);
    setStatus(null);
    setError(null);
    try {
      await onSave(commentDraft.trim() === '' ? null : commentDraft.trim());
      setStatus(
        commentDraft.trim() === ''
          ? "Comment cleared. This hand no longer shows a Jeff's comment."
          : "Saved. This comment now shows as “Jeff says” wherever this hand is practised.",
      );
    } catch {
      setError('Could not save the comment. Please try again.');
    } finally {
      setSavingComment(false);
    }
  };

  const handleSaveNote = async () => {
    setSavingNote(true);
    setStatus(null);
    setError(null);
    try {
      await onSaveNote(noteDraft.trim() === '' ? null : noteDraft.trim());
      setStatus('Notes saved.');
    } catch {
      setError('Could not save your notes. Please try again.');
    } finally {
      setSavingNote(false);
    }
  };

  return (
    <div className="bg-card border border-line rounded-lg p-4" data-testid="ask-jeff-editor">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-3">
        <h2 className="text-base font-bold text-ink">Asked under</h2>
        <span className="text-sm text-ink-soft" data-testid="ask-jeff-section-label">
          {sectionLabel}
        </span>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {/* The Super Editor's own note about the doubt. */}
        <div>
          <label htmlFor="ask-jeff-review-note" className="block text-sm font-bold text-ink mb-1">
            Your notes
          </label>
          <textarea
            id="ask-jeff-review-note"
            value={noteDraft}
            onChange={e => setNoteDraft(e.target.value)}
            rows={5}
            disabled={savingNote}
            data-testid="ask-jeff-review-note"
            className="w-full rounded-md border border-line-strong bg-paper px-3 py-2 text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-brass disabled:opacity-60"
            placeholder="No notes were written when this hand was flagged."
          />
          <button
            type="button"
            onClick={handleSaveNote}
            disabled={savingNote}
            className="mt-2 rounded-md border border-brass bg-cream px-4 py-2 text-sm font-medium text-ink hover:bg-paper disabled:opacity-50"
          >
            {savingNote ? 'Saving…' : 'Save notes'}
          </button>
        </div>

        {/* Jeff's answer. */}
        <div>
          <label htmlFor="ask-jeff-review-comment" className="block text-sm font-bold text-ink mb-1">
            Jeff's comment
          </label>
          <textarea
            id="ask-jeff-review-comment"
            value={commentDraft}
            onChange={e => setCommentDraft(e.target.value)}
            rows={5}
            disabled={savingComment}
            data-testid="ask-jeff-review-comment"
            className="w-full rounded-md border border-line-strong bg-paper px-3 py-2 text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-brass disabled:opacity-60"
            placeholder="Record what Jeff said about this hand…"
          />
          <button
            type="button"
            onClick={handleSaveComment}
            disabled={savingComment}
            data-testid="ask-jeff-save-comment"
            className="mt-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
          >
            {savingComment ? 'Saving…' : "Save Jeff's comment"}
          </button>
        </div>
      </div>

      {status && (
        <p className="mt-3 text-sm text-green-800" role="status" data-testid="ask-jeff-status">
          {status}
        </p>
      )}
      {error && (
        <p className="mt-3 text-sm text-red-700" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
